import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import React, { useState } from "react";
import Icon, { IconName } from "src/components/Icon/Icon";
import "./SideNavGroup.scss";

interface ISideNavGroupProps {
  title: string;
  iconName: IconName;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

/**
 * SideNavGroup
 * A collapsible accordion specifically styled for the left sidebar.
 * Provides separate class hooks from CollapsibleCard so we can
 * freely adjust width/height/colors without affecting other pages.
 */
const SideNavGroup: React.FC<ISideNavGroupProps> = ({ title, iconName, children, defaultOpen = true }) => {
  const [expanded, setExpanded] = useState(defaultOpen);
  const handleToggle = () => setExpanded(prev => !prev);

  return (
    <Accordion
      expanded={expanded}
      onChange={handleToggle}
      disableGutters
      elevation={0}
      square
      className={`side-nav-group ${expanded ? "open" : "collapsed"}`}
      sx={{
        background: "transparent",
        "&:before": { display: "none" },
      }}>
      <AccordionSummary
        expandIcon={
          <span className={`side-nav-group__chevron ${expanded ? "open" : ""}`}>
            <Icon name={IconName.ArrowDown} size="16px" />
          </span>
        }
        className="side-nav-group__summary"
        sx={{
          minHeight: "unset",
          padding: 0,
          "& .MuiAccordionSummary-content": {
            margin: 0,
            alignItems: "center",
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            transform: "none !important",
          },
        }}>
        <div className="side-nav-group__title">
          <Icon name={iconName} size="24px" />
          <span>{title}</span>
        </div>
      </AccordionSummary>

      <AccordionDetails className="side-nav-group__details" sx={{ padding: 0 }}>
        <nav className="side-nav-group__links">{children}</nav>
      </AccordionDetails>
    </Accordion>
  );
};

export default SideNavGroup;
